import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ADMIN_ORDER_CHARGES_UPDATE,
  ADMIN_ORDER_DETAIL,
  ADMIN_ORDER_FINAL_UPDATE,
  ADMIN_ORDER_PACKAGE_NOTES_UPDATE,
} from "../../actions/admin/customer-actions";
import {
  CUSTOMER_ORDER_DETAIL,
  CUSTOMER_ORDER_LIST,
  GET_PAYMENTS,
} from "../../actions/OrderAction";
import { GET_ADDRESS_LIST } from "../../actions/AddressActions";
import { PROFILE_DETAILS } from "../../actions/AuthActions";
import Loader from "../../components/common/loader/Loader";
import CreateOrder from "../../components/modals/create-order/CreateOrder";
import OrderItemsTable from "../../components/modals/order-view-items/OrderItemsTable";
import OrderPaymentStatus from "../../components/order/OrderPaymentStatus";
import OrderSummaryCharges from "../../components/order/OrderSummaryCharges";
import CustomerPaymentSection from "../../components/order/CustomerPaymentSection";
import {
  cloneAddressSnapshot,
  formatAddressLines,
  getOrderDeliveryAddress,
} from "../../utils/orderAddress";
import theme from "../../theme";
import { resolveTrackingStepLabels } from "../../utils/orderDisplayStatus";
import {
  getLastCompletedTrackingIndex,
  getStatusHeadline,
  getTrackingStepTimestamps,
  resolveOrderPaymentStatus,
} from "./orderTracking";
import OrderTrackingStepper from "./OrderTrackingStepper";
import { canAddProductsToOrder } from "../../utils/orderListTable";

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAdminOrdersRoute = pathname.startsWith("/admin/orders/");

  const customerOrderDetail = useSelector((s) => s?.order?.orderDetail);
  const customerOrderDetailLoading = useSelector(
    (s) => s?.order?.orderDetailLoading
  );
  const adminOrderDetail = useSelector((s) => s?.orderData?.orderDetail);
  const adminOrderDetailLoading = useSelector(
    (s) => s?.orderData?.orderDetailLoading
  );
  const adminOrderList = useSelector((s) => s?.orderData?.recordList ?? []);
  const customerOrderList = useSelector((s) => s?.order?.recordList ?? []);
  const orderChargesSaving = useSelector((s) => s?.orderData?.orderChargesSaving);
  const orderFinalUpdateLoading = useSelector((s) => s?.orderData?.orderFinalUpdateLoading);
  const orderPackageNotesSaving = useSelector((s) => s?.orderData?.orderPackageNotesSaving);

  const cachedAdminOrder = useMemo(() => {
    if (!isAdminOrdersRoute || !orderId) return null;
    return (
      adminOrderList.find((o) => String(o?.id) === String(orderId)) ?? null
    );
  }, [isAdminOrdersRoute, adminOrderList, orderId]);

  const cachedCustomerOrder = useMemo(() => {
    if (isAdminOrdersRoute || !orderId) return null;
    return (
      customerOrderList.find((o) => String(o?.id) === String(orderId)) ?? null
    );
  }, [isAdminOrdersRoute, customerOrderList, orderId]);

  const orderDetail = isAdminOrdersRoute
    ? adminOrderDetail ?? cachedAdminOrder
    : customerOrderDetail ?? cachedCustomerOrder;
  const orderDetailLoading = isAdminOrdersRoute
    ? adminOrderDetailLoading
    : customerOrderDetailLoading;
  const orderDetailError = useSelector((s) =>
    isAdminOrdersRoute ? s?.orderData?.orderDetailError : s?.order?.orderDetailError
  );

  const ordersListPath = isAdminOrdersRoute
    ? "/admin/orders"
    : "/dashboard/orders";

  const addressList = useSelector((s) => s?.address?.addressList);
  const profileDetails = useSelector((s) => s?.auth?.profileDetails);

  useEffect(() => {
    if (isAdminOrdersRoute) return;
    dispatch({ type: GET_ADDRESS_LIST });
    dispatch({ type: CUSTOMER_ORDER_LIST, payload: { page: 1, limit: 50 } });
    if (!profileDetails?.id) {
      dispatch({ type: PROFILE_DETAILS });
    }
  }, [dispatch, profileDetails?.id, isAdminOrdersRoute]);

  const fetchOrderDetail = React.useCallback(() => {
    if (!orderId) return;
    if (isAdminOrdersRoute) {
      dispatch({ type: ADMIN_ORDER_DETAIL, payload: { id: orderId } });
    } else {
      dispatch({ type: CUSTOMER_ORDER_DETAIL, payload: { id: orderId } });
    }
  }, [dispatch, orderId, isAdminOrdersRoute]);

  useEffect(() => {
    fetchOrderDetail();
    if (orderId) {
      dispatch({ type: GET_PAYMENTS, payload: { orderId } });
    }
  }, [fetchOrderDetail, dispatch, orderId]);

  /** Admin: refresh detail when returning from list after status change */
  useEffect(() => {
    if (!isAdminOrdersRoute || !orderId) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        dispatch({ type: ADMIN_ORDER_DETAIL, payload: { id: orderId } });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [dispatch, orderId, isAdminOrdersRoute]);

  const orderDeliveryAddress = useMemo(() => {
    const fromOrder = getOrderDeliveryAddress(orderDetail);
    if (fromOrder) return fromOrder;
    if (!addressList?.length) return null;
    return cloneAddressSnapshot(
      addressList.find((a) => a.is_default) || addressList[0]
    );
  }, [orderDetail, addressList]);

  const recipientName = useMemo(() => {
    const u = orderDetail?.create_by_user;
    if (u?.name?.trim()) return u.name.trim();
    const fn = u?.first_name || "";
    const ln = u?.last_name || "";
    const combined = `${fn} ${ln}`.trim();
    if (combined) return combined;
    if (profileDetails?.name) return profileDetails.name;
    const p1 = profileDetails?.first_name || "";
    const p2 = profileDetails?.last_name || "";
    return `${p1} ${p2}`.trim() || "Customer";
  }, [orderDetail?.create_by_user, profileDetails]);

  const phone = isAdminOrdersRoute
    ? orderDetail?.create_by_user?.phone_number || "—"
    : profileDetails?.phone_number ||
      orderDetail?.create_by_user?.phone_number ||
      "—";

  const order = useMemo(() => {
    if (!orderDetail) return null;
    const cached = isAdminOrdersRoute ? cachedAdminOrder : cachedCustomerOrder;
    const status = orderDetail.order_status ?? cached?.order_status;
    const payment_status =
      orderDetail.payment_status ?? cached?.payment_status;
    if (
      (status == null || status === orderDetail.order_status) &&
      (payment_status == null || payment_status === orderDetail.payment_status)
    ) {
      return orderDetail;
    }
    return {
      ...orderDetail,
      ...(status != null ? { order_status: status } : {}),
      ...(payment_status != null ? { payment_status } : {}),
    };
  }, [
    orderDetail,
    isAdminOrdersRoute,
    cachedAdminOrder,
    cachedCustomerOrder,
  ]);
  const lastIdx = order ? getLastCompletedTrackingIndex(order.order_status) : -2;
  const stageTimestamps = getTrackingStepTimestamps(order);
  const stepLabels = useMemo(() => resolveTrackingStepLabels(order), [order]);
  const { title: statusTitle, sub: statusSub } = getStatusHeadline(order);
  const isRejected =
    String(order?.order_status || "").toLowerCase() === "rejected";
  // eslint-disable-next-line no-unused-vars
  const isAddProductsDisabled = !canAddProductsToOrder(order);

  const details = order?.order_details || [];
  const [addProductsOpen, setAddProductsOpen] = React.useState(false);

  const handleSaveCharges = (charges, onSuccess) => {
    if (!orderId) return;
    dispatch({
      type: ADMIN_ORDER_CHARGES_UPDATE,
      payload: {
        orderId,
        charges,
        onSuccess: () => {
          fetchOrderDetail();
          onSuccess?.();
        },
      },
    });
  };

  const handleFinalUpdateToCustomer = (invoiceId) => {
    if (!orderId) return;
    dispatch({
      type: ADMIN_ORDER_FINAL_UPDATE,
      payload: {
        orderId,
        invoiceId,
        onSuccess: () => fetchOrderDetail(),
      },
    });
  };

  const handleSavePackageNotes = (package_notes, onSuccess) => {
    if (!orderId) return;
    dispatch({
      type: ADMIN_ORDER_PACKAGE_NOTES_UPDATE,
      payload: {
        orderId,
        package_notes,
        onSuccess: () => {
          fetchOrderDetail();
          onSuccess?.();
        },
      },
    });
  };

  if (!order && (orderDetailLoading || orderDetailError == null)) {
    return (
      <Box sx={{ py: 6 }}>
        <Loader minHeight={320} size={48} />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3, width: "100%" }}>
        <Button startIcon={<ChevronLeftIcon />} onClick={() => navigate(ordersListPath)}>
          Back to orders
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        pb: 4,
        pt: 2,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Button
          size="small"
          startIcon={<ChevronLeftIcon />}
          onClick={() => navigate(ordersListPath)}
          sx={{ mb: 2, color: theme.palette.custom.primary }}
        >
          Back to orders
        </Button>

        {/* Status + tracking */}
        <Card elevation={0} sx={{ borderRadius: 2, mb: 2, border: "1px solid #e8eaed" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} color="#111">
                    {statusTitle}
                  </Typography>
                  {isRejected && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: "20px",
                        bgcolor: "#fff",
                        color: "#9B0012",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        border: "1px solid #FF3850",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#FF3850",
                          flexShrink: 0,
                        }}
                      />
                      Rejected
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {statusSub}
                </Typography>
              </Box>
              <OrderPaymentStatus paymentStatus={resolveOrderPaymentStatus(order)} />
            </Box>

            {lastIdx >= 0 ? (
              <OrderTrackingStepper
                lastCompletedIndex={lastIdx}
                stageTimestamps={stageTimestamps}
                stepLabels={stepLabels}
              />
            ) : (
              <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                Tracking is not available for this order status.
              </Typography>
            )}
          </CardContent>
        </Card>

        <CustomerPaymentSection
          orderId={orderId}
          order={order}
          adminView={isAdminOrdersRoute}
          recipientName={recipientName}
          onPaymentSuccess={fetchOrderDetail}
        />

        {/* <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ borderRadius: 2, height: "100%", border: "1px solid #e8eaed" }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>
                  Need help with this order?
                </Typography>
                <Link href="/dashboard/help-center" underline="hover" sx={{ display: "block", mb: 1 }}>
                  Request item return / refund
                </Link>
                <Link href="/dashboard/help-center" underline="hover" sx={{ display: "block" }}>
                  Contact Customer Service
                </Link>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ borderRadius: 2, height: "100%", border: "1px solid #e8eaed" }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>
                  Buy these items again?
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    mt: 1,
                    borderRadius: 10,
                    bgcolor: "#FFD814",
                    color: "#111",
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#F7CA00", boxShadow: "none" },
                  }}
                  disabled
                >
                  Add all items to cart
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Coming soon
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid> */}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ borderRadius: 2, height: "100%", border: "1px solid #e8eaed", width: "100%" }}>
              <CardContent sx={{ pt: 2, px: 2, pb: 2, "&:last-child": { pb: 2 } }}>
                <Typography fontWeight={700} gutterBottom sx={{ mb: 1 }}>
                  Delivery address
                </Typography>
                {orderDeliveryAddress ? (
                  <>
                    <Typography fontWeight={700}>{recipientName}</Typography>
                    {formatAddressLines(orderDeliveryAddress).map((line, index) => (
                      <Typography
                        key={`${index}-${line}`}
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: index === 0 ? 1 : 0.5, whiteSpace: "pre-line" }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No delivery address on file. Add one in{" "}
                    <Link component="button" variant="body2" onClick={() => navigate("/dashboard/profile")}>
                      Profile
                    </Link>{" "}
                    or edit after creating an order with an address.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              elevation={0}
              sx={{ borderRadius: 2, height: "100%", border: "1px solid #e8eaed", width: "100%" }}
            >
              <CardContent sx={{ pt: 2, px: 2, pb: 2, "&:last-child": { pb: 2 } }}>
                <Typography fontWeight={700} gutterBottom sx={{ mb: 1 }}>
                  Your mobile number
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  We use your number to notify you about order status and may call you to coordinate
                  your delivery or pickup.
                </Typography>
                <Typography fontWeight={700} component="p" sx={{ m: 0 }}>
                  {phone}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e8eaed", width: "100%" }}>
              <CardContent sx={{ pt: 2, px: 2, pb: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography fontWeight={700}>Items in this order</Typography>
                  {/* {!isAdminOrdersRoute && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={isAddProductsDisabled}
                      onClick={() => setAddProductsOpen(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Add items
                    </Button>
                  )} */}
                </Box>
                <OrderItemsTable orderDetails={details} compact hidePrices={isAdminOrdersRoute} orderStatus={order?.order_status} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e8eaed", width: "100%" }}>
              <CardContent>
                <OrderSummaryCharges
                  order={order}
                  editable={isAdminOrdersRoute}
                  adminView={isAdminOrdersRoute}
                  saving={!!orderChargesSaving}
                  recipientName={recipientName}
                  onSave={isAdminOrdersRoute ? handleSaveCharges : undefined}
                  onFinalUpdate={isAdminOrdersRoute ? handleFinalUpdateToCustomer : undefined}
                  finalUpdateLoading={!!orderFinalUpdateLoading}
                  onSavePackageNotes={
                    isAdminOrdersRoute ? handleSavePackageNotes : undefined
                  }
                  packageNotesSaving={!!orderPackageNotesSaving}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {orderId && order && (
        <>
          <CreateOrder
            open={addProductsOpen}
            onClose={() => setAddProductsOpen(false)}
            mode="append"
            orderId={orderId}
            existingOrderDetails={details}
          />
        </>
      )}
    </Box>
  );
}

